import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { useCompleteUserVoucherOnboardingMutation } from '../services/benefitApi';
import { VOUCHER_ONBOARDING_PLATFORM_IBK, VOUCHER_ONBOARDING_TYPE_WELCOME } from '../types';
import { useBenefitsOnboardingEntry } from './useBenefitsOnboardingEntry';
import { useWelcomeModalWithRecharges } from './useWelcomeModalWithRecharges';
function isHttpConflict(error) {
    if (typeof error !== 'object' || error === null || !('status' in error)) {
        return false;
    }
    const { status } = error;
    return status === 409 || status === '409';
}
export function useHomePageBenefitsWelcome({ contract, isOnboardingFeatureEnabled: onboardingFf, isOnboardingEligible = true, onLegacyPrimaryAction, }) {
    const { t } = useTranslation();
    const { shouldShowModal, dismissModal } = useWelcomeModalWithRecharges({
        contract,
        enabled: !onboardingFf,
    });
    const { shouldShowOnboardingEntry } = useBenefitsOnboardingEntry({
        contract,
        enabled: onboardingFf,
        isEligible: isOnboardingEligible,
    });
    const [completeUserVoucherOnboarding] = useCompleteUserVoucherOnboardingMutation();
    const welcomeOpenPersistedRef = useRef(false);
    const [step, setStep] = useState('idle');
    useEffect(() => {
        if (!onboardingFf) {
            setStep('idle');
            welcomeOpenPersistedRef.current = false;
        }
    }, [onboardingFf]);
    useEffect(() => {
        welcomeOpenPersistedRef.current = false;
    }, [contract.id]);
    useEffect(() => {
        if (onboardingFf && shouldShowOnboardingEntry && step === 'idle') {
            setStep('entry');
        }
    }, [onboardingFf, shouldShowOnboardingEntry, step]);
    useEffect(() => {
        if (!onboardingFf || step !== 'entry' || welcomeOpenPersistedRef.current) {
            return;
        }
        welcomeOpenPersistedRef.current = true;
        completeUserVoucherOnboarding({
            platform: VOUCHER_ONBOARDING_PLATFORM_IBK,
            type: VOUCHER_ONBOARDING_TYPE_WELCOME,
        })
            .unwrap()
            .catch((error) => {
            if (isHttpConflict(error)) {
                return;
            }
            welcomeOpenPersistedRef.current = false;
        });
    }, [onboardingFf, step, completeUserVoucherOnboarding]);
    const startOnboardingFromWelcome = useCallback(() => {
        setStep('tour');
    }, []);
    const finishOnboarding = useCallback(() => {
        setStep('idle');
    }, []);
    const isLegacyOpen = !onboardingFf && shouldShowModal;
    const isOnboardingEntryOpen = onboardingFf && step === 'entry';
    const welcomeIsOpen = isLegacyOpen || isOnboardingEntryOpen;
    const welcomeModalProps = isOnboardingEntryOpen
        ? {
            isOpen: welcomeIsOpen,
            onClose: () => setStep('skip'),
            onPrimaryAction: startOnboardingFromWelcome,
            closeOnPrimaryAction: false,
            title: t('benefits.onboarding.intro.title'),
            description: t('benefits.onboarding.intro.description'),
            primaryActionLabel: t('benefits.onboarding.intro.start'),
            primaryActionLeftIcon: 'none',
            primaryActionRightIcon: 'arrowLineRight',
            onboardingWelcomeActions: true,
            secondaryActionLabel: t('benefits.onboarding.intro.skip'),
            onSecondaryAction: () => setStep('skip'),
        }
        : {
            isOpen: welcomeIsOpen,
            onClose: dismissModal,
            onPrimaryAction: onLegacyPrimaryAction,
        };
    const benefitsOnboardingWizard = onboardingFf && (step === 'tour' || step === 'skip')
        ? {
            mode: step === 'tour' ? 'tour' : 'skip',
            onReopenFromSkipCancel: () => setStep('entry'),
            onFlowFinished: finishOnboarding,
        }
        : null;
    return { welcomeModalProps, benefitsOnboardingWizard };
}
